import {
  IAnalysisQueryResponse,
  IAnalysisResponse,
} from '../../../apollo/responseTypes';
import {
  AttributionKey,
  ExpressionModeKey,
  FunnelModeKey,
  FunnelOrderKey,
  GroupbyKey,
  IBreakdownFieldsFragment,
  ICohortRefFieldsFragment,
  IMeasurePropertiesFieldsFragment,
  ISimpleMeasurePropertiesFieldsFragment,
  PathModeKey,
  RetentionViewKey,
  SignificanceModeKey,
  TimeUnit,
} from '../../../apollo/graphqlSchema';
import moment from 'moment';
import {
  IAnalyticsAnalysisArgument,
  IAnalyticsEventReportProperties,
} from './types';
import { UnreachableCaseError } from 'ts-essentials';
import {
  IAnalyticsAdditionalSettings,
  IAnalyticsLoggedEvent,
  IAnalyticsLoggedField,
  IAnalyticsMeasure,
} from '../../types';
import { isNil, uniqBy } from 'lodash';
import {
  getEventNamesFromMeasure,
  getFieldNamesFromMeasure,
  getMeasures,
} from '../measure/utils';
import { getCreator } from '../user/utils';
import {
  getEventNamesFromEventCondition,
  getEventNamesFromEventConditionCollection,
  getFieldNamesFromEventCondition,
  getFieldNamesFromEventConditionCollection,
  uniqueAnalyticsLoggedEventFunction,
} from '../event/utils';
import { MaybeNil } from '../../../interfaces';
import {
  getFieldNamesFromFilter,
  getFieldNamesFromPredicate,
} from '../filter/utils';
import {
  getFieldName,
  uniqueAnalyticsLoggedFieldFunction,
} from '../field/utils';
import { IAnalysisMetadata } from '../../../../redux/slices/analyses';
import { assertNotNil } from '../../../types';

const reportTypeMap = {
  ExpressionQuery: 'query',
  FunnelQuery: 'funnel',
  RetentionQuery: 'retention',
  PathQuery: 'path',
  PredictionQuery: 'prediction',
  DataTableQuery: 'data table',
  SignificanceQuery: 'significance',
  CohortSampleQuery: 'cohort',
} satisfies Record<IAnalysisQueryResponse['__typename'], string>;

const timeUnitMap = {
  [TimeUnit.Second]: 'second',
  [TimeUnit.Minute]: 'minute',
  [TimeUnit.Hour]: 'hour',
  [TimeUnit.Day]: 'day',
  [TimeUnit.Week]: 'week',
  [TimeUnit.FourWeek]: 'four week',
  [TimeUnit.Month]: 'month',
  [TimeUnit.Quarter]: 'quarter',
  [TimeUnit.Year]: 'year',
  [TimeUnit.AllTime]: 'all time',
  [TimeUnit.Na]: undefined,
} satisfies Record<TimeUnit, string | undefined>;

const hasBreakdown = (query: IAnalysisQueryResponse) => {
  if ('breakdown' in query) {
    return (query.breakdown?.groupbys.length ?? 0) > 0;
  } else {
    return false;
  }
};

const hasSegment = (query: IAnalysisQueryResponse) => {
  if ('breakdown' in query) {
    return (query.breakdown?.cohortRefs.length ?? 0) > 0;
  } else {
    return false;
  }
};

const getDateRangeDays = (analysis: Pick<IAnalysisResponse, 'start' | 'end'>) =>
  moment(analysis.end).diff(moment(analysis.start), 'days') + 1; // report with same start and end days have length of 1

const funnelOrderToAnalyticsValue: Record<FunnelOrderKey, string> = {
  [FunnelOrderKey.Any]: 'any',
  [FunnelOrderKey.Exact]: 'exact',
  [FunnelOrderKey.Sequence]: 'sequence',
  [FunnelOrderKey.Custom]: 'custom',
};

const groupByToAnalyticsValue: Record<GroupbyKey, string> = {
  [GroupbyKey.Global]: 'global',
  [GroupbyKey.Starting]: 'starting',
};

const attributionToAnalyticsValue: Record<AttributionKey, string> = {
  [AttributionKey.First]: 'first',
  [AttributionKey.Linear]: 'linear',
};

const getAdditionalSettings = (
  query: IAnalysisQueryResponse,
): IAnalyticsAdditionalSettings => {
  const settings: IAnalyticsAdditionalSettings = {};
  if ('samplePercentage' in query && !isNil(query.samplePercentage)) {
    settings.sample_percentage = query.samplePercentage;
  }
  if ('sampleAbsolute' in query && !isNil(query.sampleAbsolute)) {
    settings.sample_absolute = query.sampleAbsolute;
  }
  if ('groupby' in query && query.groupby) {
    settings.group_by = groupByToAnalyticsValue[query.groupby.key];
  }
  if (query.__typename === 'SignificanceQuery') {
    settings.subject = query.significanceSubject.name;
  } else {
    if ('subject' in query && query.subject) {
      settings.subject = query.subject.name;
    }
  }
  if ('partitions' in query) {
    settings.partitions = query.partitions.map((p) => p.partition.name);
  }
  if (query.__typename === 'FunnelQuery') {
    settings.order = funnelOrderToAnalyticsValue[query.order.key];
  } else if (query.__typename === 'RetentionQuery') {
    if (query.attribution) {
      settings.attribution = attributionToAnalyticsValue[query.attribution.key];
    }
  }

  return settings;
};

const getMeasuresFromQuery = (
  query: IAnalysisQueryResponse,
): IAnalyticsMeasure[] | undefined => {
  if (query.__typename === 'ExpressionQuery') {
    return getMeasures(query.measure);
  } else if (query.__typename === 'DataTableQuery') {
    return query.measures.flatMap(getMeasures);
  }
};

const simpleMeasureHasFilter = (
  measureProps: ISimpleMeasurePropertiesFieldsFragment,
) => {
  return !isNil(measureProps.filter) || measureProps.cohortRefs.length > 0;
};

const hasMeasureFilter = (measureProps: IMeasurePropertiesFieldsFragment) => {
  if (measureProps.__typename === 'SimpleMeasureProperties') {
    return simpleMeasureHasFilter(measureProps);
  } else if (measureProps.__typename === 'CompoundMeasureProperties') {
    return (
      !isNil(measureProps.resultFilter) ||
      measureProps.measures.some(simpleMeasureHasFilter)
    );
  } else {
    throw new UnreachableCaseError(measureProps);
  }
};

const getFilterType = (query: IAnalysisResponse['query']) => {
  const filters: string[] = [];
  if ('filter' in query) {
    if (query.cohortRefs.length > 0) {
      filters.push('cohort filter');
    }
    if (query.filter && query.filter.conditions.length > 0) {
      filters.push('global filter');
    }
  }

  if (query.__typename === 'DataTableQuery') {
    if (query.measuresProperties.some(hasMeasureFilter)) {
      filters.push('measure filter');
    }
  } else if (query.__typename === 'ExpressionQuery') {
    if (hasMeasureFilter(query.measureProperties)) {
      filters.push('measure filter');
    }
  }

  return filters.length > 0 ? filters : undefined;
};

const expressionModeKeyToAnalyticsValue: Record<ExpressionModeKey, string> = {
  [ExpressionModeKey.Query]: 'query',
  [ExpressionModeKey.Impact]: 'impact analysis',
  [ExpressionModeKey.Histogram]: 'histogram',
};

const funnelModeKeyToAnalyticsValue: Record<FunnelModeKey, string> = {
  [FunnelModeKey.Conversion]: 'conversion',
  [FunnelModeKey.TimeToConvert]: 'time to convert',
  [FunnelModeKey.Frequency]: 'frequency',
};

const retentionViewKeyToAnalyticsValue: Record<RetentionViewKey, string> = {
  [RetentionViewKey.Retention]: 'retention',
  [RetentionViewKey.RetentionOverTime]: 'retention over time',
  [RetentionViewKey.UsageInterval]: 'usage interval',
  [RetentionViewKey.UsageIntervalOverTime]: 'usage interval over time',
};

const pathModeKeyToAnalyticsValue: Record<PathModeKey, string> = {
  [PathModeKey.Sankey]: 'sankey',
  [PathModeKey.TopPaths]: 'top paths',
};

const significanceModeKeyToAnalyticsValue: Record<SignificanceModeKey, string> =
  {
    [SignificanceModeKey.Breakdown]: 'breakdown',
    [SignificanceModeKey.Experiment]: 'experiment',
    [SignificanceModeKey.Segment]: 'segment',
  };

const getReportMode = (query: IAnalysisQueryResponse) => {
  if (query.__typename === 'ExpressionQuery') {
    return expressionModeKeyToAnalyticsValue[query.expressionMode.key];
  } else if (query.__typename === 'FunnelQuery') {
    if (query.mode?.key) {
      return funnelModeKeyToAnalyticsValue[query.mode.key];
    } else {
      return undefined;
    }
  } else if (query.__typename === 'RetentionQuery') {
    return retentionViewKeyToAnalyticsValue[query.view.key];
  } else if (query.__typename === 'PathQuery') {
    return pathModeKeyToAnalyticsValue[query.pathMode.key];
  } else if (query.__typename === 'PredictionQuery') {
    return undefined;
  } else if (query.__typename === 'DataTableQuery') {
    return undefined;
  } else if (query.__typename === 'SignificanceQuery') {
    return significanceModeKeyToAnalyticsValue[query.significanceMode.key];
  } else if (query.__typename === 'CohortSampleQuery') {
    return undefined;
  } else {
    throw new UnreachableCaseError(query);
  }
};

const getEventNamesFromCohort = (
  cohort: ICohortRefFieldsFragment['cohort'],
): IAnalyticsLoggedEvent[] => {
  if (cohort.__typename === 'PerformedCohort') {
    return getEventNamesFromEventConditionCollection(cohort.eventConditions);
  } else if (cohort.__typename === 'MeasureCohort') {
    return getEventNamesFromMeasure(cohort.measure);
  } else if (cohort.__typename === 'CompoundCohort') {
    return cohort.simpleCohorts.flatMap(getEventNamesFromCohort);
  } else if (cohort.__typename === 'ExpressionCohort') {
    return [];
  } else if (cohort.__typename === 'FunnelCohort') {
    return [];
  } else if (cohort.__typename === 'RetentionCohort') {
    return [];
  } else {
    throw new UnreachableCaseError(cohort);
  }
};

const getEventNamesFromCohortRef = (cohortRef: ICohortRefFieldsFragment) =>
  getEventNamesFromCohort(cohortRef.cohort);

const getEventNamesFromCohortRefCollection = (
  cohortRefs: MaybeNil<ICohortRefFieldsFragment[]>,
) => (cohortRefs ?? []).flatMap(getEventNamesFromCohortRef);

const getAllEvents = (
  query: IAnalysisQueryResponse,
): IAnalyticsLoggedEvent[] | undefined => {
  if (query.__typename === 'ExpressionQuery') {
    return [
      ...getEventNamesFromMeasure(query.measure),
      ...getEventNamesFromEventCondition(query.triggerCondition),
      ...getEventNamesFromCohortRefCollection(query.breakdown?.cohortRefs),
      ...getEventNamesFromCohortRefCollection(query.cohortRefs),
    ];
  } else if (query.__typename === 'FunnelQuery') {
    return [
      ...getEventNamesFromCohortRefCollection(query.breakdown?.cohortRefs),
      ...getEventNamesFromCohortRefCollection(query.cohortRefs),
      ...[...query.steps, ...query.negativeSteps].flatMap((s) =>
        getEventNamesFromEventConditionCollection(s.stepConditions),
      ),
    ];
  } else if (query.__typename === 'PathQuery') {
    return [
      ...getEventNamesFromCohortRefCollection(query.breakdown?.cohortRefs),
      ...getEventNamesFromCohortRefCollection(query.cohortRefs),
      ...[
        ...query.startConditions,
        ...query.endConditions,
        ...query.expandEvents.map((ee) => ee.eventCondition),
        ...query.mergeEvents.flatMap((me) => me.eventConditions),
        ...query.foldEvents.map((fe) => fe.eventCondition),
      ].flatMap(getEventNamesFromEventCondition),
    ];
  } else if (query.__typename === 'RetentionQuery') {
    return [
      ...getEventNamesFromCohortRefCollection(query.breakdown?.cohortRefs),
      ...getEventNamesFromCohortRefCollection(query.cohortRefs),
      ...[...query.startConditions, ...query.returnConditions].flatMap(
        getEventNamesFromEventCondition,
      ),
    ];
  } else if (query.__typename === 'PredictionQuery') {
    return [
      ...getEventNamesFromCohortRef(query.sourceCohortRef),
      ...getEventNamesFromCohortRef(query.targetCohortRef),
      ...getEventNamesFromEventCondition(query.eventCondition),
    ];
  } else if (query.__typename === 'DataTableQuery') {
    return [
      ...getEventNamesFromCohortRefCollection(query.cohortRefs),
      ...getEventNamesFromCohortRefCollection(query.breakdown?.cohortRefs),
      ...query.measures.flatMap(getEventNamesFromMeasure),
    ];
  } else if (query.__typename === 'CohortSampleQuery') {
    return [
      ...getEventNamesFromCohort(query.cohort),
      ...(!isNil(query.compare)
        ? [
            ...query.compare.expandEvents.map((ee) => ee.eventCondition),
            ...query.compare.mergeEvents.flatMap((me) => me.eventConditions),
          ].flatMap(getEventNamesFromEventCondition)
        : []),
    ];
  } else if (query.__typename === 'SignificanceQuery') {
    const events: IAnalyticsLoggedEvent[] = [];
    const criteria = query.criteria;
    if (criteria.__typename === 'SegmentCriteria') {
      events.push(...getEventNamesFromCohort(criteria.segmentControlGroup));
      events.push(...criteria.segmentVariants.flatMap(getEventNamesFromCohort));
    } else if (criteria.__typename === 'BreakdownCriteria') {
      // no events
    } else if (criteria.__typename === 'ExperimentCriteria') {
      // no events
    } else {
      throw new UnreachableCaseError(criteria);
    }
    const metric = query.metric;
    if (metric.__typename === 'MeasureMetric') {
      events.push(...getEventNamesFromMeasure(metric.measure));
    } else if (metric.__typename === 'QueryMetric') {
      events.push(...getEventNamesFromMeasure(metric.measure));
    } else if (metric.__typename === 'RetentionMetric') {
      // no events
    } else if (metric.__typename === 'FunnelMetric') {
      // no events
    } else {
      throw new UnreachableCaseError(metric);
    }
    return [
      ...events,
      ...getEventNamesFromCohortRefCollection(query.cohortRefs),
    ];
  } else {
    throw new UnreachableCaseError(query);
  }
};

const getUniqueEvents = (query: IAnalysisQueryResponse) =>
  uniqBy(getAllEvents(query), uniqueAnalyticsLoggedEventFunction);

const getFieldNamesFromCohort = (
  cohort: ICohortRefFieldsFragment['cohort'],
): IAnalyticsLoggedField[] => {
  if (cohort.__typename === 'PerformedCohort') {
    return [
      ...getFieldNamesFromEventConditionCollection(cohort.eventConditions),
      ...getFieldNamesFromFilter(cohort.filter),
    ];
  } else if (cohort.__typename === 'MeasureCohort') {
    return [
      ...getFieldNamesFromMeasure(cohort.measure),
      ...getFieldNamesFromFilter(cohort.filter),
    ];
  } else if (cohort.__typename === 'CompoundCohort') {
    return cohort.simpleCohorts.flatMap(getFieldNamesFromCohort);
  } else if (cohort.__typename === 'ExpressionCohort') {
    return getFieldNamesFromFilter(cohort.filter);
  } else if (cohort.__typename === 'FunnelCohort') {
    return getFieldNamesFromFilter(cohort.filter);
  } else if (cohort.__typename === 'RetentionCohort') {
    return getFieldNamesFromFilter(cohort.filter);
  } else {
    throw new UnreachableCaseError(cohort);
  }
};

const getFieldNamesFromCohortRef = (cohortRef: ICohortRefFieldsFragment) =>
  getFieldNamesFromCohort(cohortRef.cohort);

const getFieldNamesFromCohortRefCollection = (
  cohortRefs: MaybeNil<ICohortRefFieldsFragment[]>,
) => (cohortRefs ?? []).flatMap(getFieldNamesFromCohortRef);

const getFieldNamesFromBreakdownGroupBys = (
  groupbys: MaybeNil<IBreakdownFieldsFragment['groupbys']>,
) => {
  if (isNil(groupbys)) {
    return [];
  }
  return groupbys.flatMap((gb) => {
    return [
      ...getFieldNamesFromPredicate(gb.predicate),
      getFieldName(gb.field),
    ];
  });
};

const getFieldNamesFromMeasureProperties = (
  measureProps: IMeasurePropertiesFieldsFragment,
): IAnalyticsLoggedField[] => {
  if (measureProps.__typename === 'SimpleMeasureProperties') {
    return [...getFieldNamesFromCohortRefCollection(measureProps.cohortRefs)];
  } else if (measureProps.__typename === 'CompoundMeasureProperties') {
    return measureProps.measures.flatMap(getFieldNamesFromMeasureProperties);
  } else {
    throw new UnreachableCaseError(measureProps);
  }
};

const getAllFields = (query: IAnalysisQueryResponse) => {
  if (query.__typename === 'ExpressionQuery') {
    return [
      ...getFieldNamesFromFilter(query.filter),
      ...getFieldNamesFromBreakdownGroupBys(query.breakdown?.groupbys),
      ...getFieldNamesFromMeasure(query.measure),
      ...getFieldNamesFromMeasureProperties(query.measureProperties),
      ...getFieldNamesFromEventCondition(query.triggerCondition),
      ...getFieldNamesFromCohortRefCollection(query.breakdown?.cohortRefs),
      ...getFieldNamesFromCohortRefCollection(query.cohortRefs),
    ];
  } else if (query.__typename === 'FunnelQuery') {
    return [
      ...getFieldNamesFromFilter(query.filter),
      ...getFieldNamesFromBreakdownGroupBys(query.breakdown?.groupbys),
      ...getFieldNamesFromCohortRefCollection(query.breakdown?.cohortRefs),
      ...getFieldNamesFromCohortRefCollection(query.cohortRefs),
      ...[...query.steps, ...query.negativeSteps].flatMap((s) =>
        getFieldNamesFromEventConditionCollection(s.stepConditions),
      ),
    ];
  } else if (query.__typename === 'PathQuery') {
    return [
      ...getFieldNamesFromFilter(query.filter),
      ...getFieldNamesFromBreakdownGroupBys(query.breakdown?.groupbys),
      ...getFieldNamesFromCohortRefCollection(query.breakdown?.cohortRefs),
      ...getFieldNamesFromCohortRefCollection(query.cohortRefs),
      ...[
        ...query.startConditions,
        ...query.endConditions,
        ...query.expandEvents.map((ee) => ee.eventCondition),
        ...query.mergeEvents.flatMap((me) => me.eventConditions),
        ...query.foldEvents.map((fe) => fe.eventCondition),
      ].flatMap(getFieldNamesFromEventCondition),
    ];
  } else if (query.__typename === 'RetentionQuery') {
    return [
      ...getFieldNamesFromFilter(query.filter),
      ...getFieldNamesFromBreakdownGroupBys(query.breakdown?.groupbys),
      ...getFieldNamesFromCohortRefCollection(query.breakdown?.cohortRefs),
      ...getFieldNamesFromCohortRefCollection(query.cohortRefs),
      ...[...query.startConditions, ...query.returnConditions].flatMap(
        getFieldNamesFromEventCondition,
      ),
    ];
  } else if (query.__typename === 'PredictionQuery') {
    return [
      ...getFieldNamesFromCohortRef(query.sourceCohortRef),
      ...getFieldNamesFromCohortRef(query.targetCohortRef),
      ...getFieldNamesFromEventCondition(query.eventCondition),
    ];
  } else if (query.__typename === 'DataTableQuery') {
    return [
      ...getFieldNamesFromFilter(query.filter),
      ...getFieldNamesFromCohortRefCollection(query.cohortRefs),
      ...getFieldNamesFromBreakdownGroupBys(query.breakdown?.groupbys),
      ...getFieldNamesFromCohortRefCollection(query.breakdown?.cohortRefs),
      ...query.measures.flatMap(getFieldNamesFromMeasure),
    ];
  } else if (query.__typename === 'CohortSampleQuery') {
    return [
      ...getFieldNamesFromCohort(query.cohort),
      ...(!isNil(query.compare)
        ? [
            ...query.compare.expandEvents.map((ee) => ee.eventCondition),
            ...query.compare.mergeEvents.flatMap((me) => me.eventConditions),
          ].flatMap(getFieldNamesFromEventCondition)
        : []),
    ];
  } else if (query.__typename === 'SignificanceQuery') {
    const fields: IAnalyticsLoggedField[] = [];
    const criteria = query.criteria;
    if (criteria.__typename === 'SegmentCriteria') {
      fields.push(...getFieldNamesFromCohort(criteria.segmentControlGroup));
      fields.push(...criteria.segmentVariants.flatMap(getFieldNamesFromCohort));
    } else if (criteria.__typename === 'BreakdownCriteria') {
      fields.push(getFieldName(criteria.field));
    } else if (criteria.__typename === 'ExperimentCriteria') {
      fields.push(
        getFieldName(criteria.experimentField),
        getFieldName(criteria.variationField),
      );
    } else {
      throw new UnreachableCaseError(criteria);
    }
    const metric = query.metric;
    if (metric.__typename === 'MeasureMetric') {
      fields.push(...getFieldNamesFromMeasure(metric.measure));
    } else if (metric.__typename === 'QueryMetric') {
      fields.push(...getFieldNamesFromMeasure(metric.measure));
    } else if (metric.__typename === 'RetentionMetric') {
      // no fields
    } else if (metric.__typename === 'FunnelMetric') {
      // no fields
    } else {
      throw new UnreachableCaseError(metric);
    }
    return [
      ...fields,
      ...getFieldNamesFromFilter(query.filter),
      ...getFieldNamesFromCohortRefCollection(query.cohortRefs),
    ];
  } else {
    throw new UnreachableCaseError(query);
  }
};

const getUniqueFields = (query: IAnalysisQueryResponse) =>
  uniqBy(getAllFields(query), uniqueAnalyticsLoggedFieldFunction);

export const getReportEventPropertiesFromAnalysisMetaData = (
  metadata: IAnalysisMetadata,
): IAnalyticsEventReportProperties => {
  const { query, analysisId, display, timeUnit, startDate, endDate, creator } =
    metadata;

  return getReportEventPropertiesFromAnalysisEntity(
    {
      query,
      display,
      id: analysisId,
      timeUnit,
      start: startDate,
      end: endDate,
      creator,
    },
    false,
  );
};

export const getReportEventPropertiesFromAnalysisEntity = (
  analysis: IAnalyticsAnalysisArgument,
  isEmbedded?: boolean,
): IAnalyticsEventReportProperties => {
  assertNotNil(analysis);
  const { query, display, id, timeUnit, start, end, creator } = analysis;
  const filterType = getFilterType(query);
  return {
    object_type: 'report',
    object_id: id,
    object_name: display,
    schema_name: query.schema.name,
    report_type: reportTypeMap[query.__typename],
    date_range_units: timeUnitMap[timeUnit],
    additional_settings: getAdditionalSettings(query),
    date_range_days: getDateRangeDays({
      start,
      end,
    }),
    filter_type: filterType,
    has_breakdown: hasBreakdown(query),
    has_filter: !isNil(filterType),
    has_segment: hasSegment(query),
    object_owner: getCreator({
      creator,
    }),
    report_mode: getReportMode(query),
    measures: getMeasuresFromQuery(query),
    events: getUniqueEvents(query),
    fields: getUniqueFields(query),
    is_embedded: isEmbedded ?? false,
  };
};
